import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Paper,
  Divider 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import PersonIcon from '@mui/icons-material/Person';
import FeedIcon from '@mui/icons-material/Feed';
import SecurityIcon from '@mui/icons-material/Security';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BuildIcon from '@mui/icons-material/Build';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LockResetIcon from '@mui/icons-material/LockReset';
import EditIcon from '@mui/icons-material/Edit';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PolicyIcon from '@mui/icons-material/Policy';
import QuizIcon from '@mui/icons-material/Quiz';
import Reports from './Reports';

const StyledContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
  padding: theme.spacing(4),
}));

const PageSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: '#fff',
  borderRadius: '10px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
}));

const PageButton = styled(Button)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  textTransform: 'none',
  justifyContent: 'flex-start',
  backgroundColor: '#fff',
  color: '#333',
  border: '1px solid #e0e0e0',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#FFD700',
    color: '#000',
  },
}));

const Pages = () => {
  const navigate = useNavigate();

  const pageGroups = [
    {
      title: 'Main Pages',
      pages: [
        { path: '/', name: 'Home', icon: <HomeIcon /> },
        { path: '/dashboard', name: 'Dashboard', icon: <DashboardIcon /> },
        { path: '/space', name: 'Space Dashboard', icon: <DashboardIcon /> },
        { path: '/feed', name: 'Feed', icon: <FeedIcon /> },
        { path: '/hardware', name: 'Hardware', icon: <BuildIcon /> },
        { path: '/solutions', name: 'Solutions', icon: <SmartToyIcon /> },
        { path: '/options', name: 'Options', icon: <SettingsIcon /> },
      ]
    },
    {
      title: 'Questions & Answers',
      pages: [
        { path: '/questions', name: 'Questions', icon: <QuestionAnswerIcon /> },
        { path: '/all-questions', name: 'All Questions', icon: <QuizIcon /> },
        { path: '/ask-question', name: 'Ask Question', icon: <QuestionAnswerIcon /> },
        { path: '/ai-detect', name: 'AI Detection', icon: <SecurityIcon /> },
      ]
    },
    {
      title: 'User Management',
      pages: [
        { path: '/login', name: 'Login', icon: <VpnKeyIcon /> },
        { path: '/join', name: 'Join', icon: <PersonIcon /> },
        { path: '/profile', name: 'Profile', icon: <PersonIcon /> },
        { path: '/manage-profile', name: 'Manage Profile', icon: <SettingsIcon /> },
        { path: '/edit-profile', name: 'Edit Profile', icon: <EditIcon /> },
        { path: '/forgot-password', name: 'Forgot Password', icon: <LockResetIcon /> },
        { path: '/reset-password', name: 'Reset Password', icon: <LockResetIcon /> },
      ]
    },
    {
      title: 'Additional Pages',
      pages: [
        { path: '/pricing', name: 'Pricing', icon: <PriceChangeIcon /> },
        { path: '/premium-access', name: 'Premium Access', icon: <SecurityIcon /> },
        { path: '/privacy', name: 'Privacy', icon: <PolicyIcon /> },
      ]
    },
    {
      title: 'Reports',
      pages: [
        { path: '/reports', name: 'Reports', icon: <Reports /> },
      ]
    },
  ];

  return (
    <StyledContainer>
      <Container maxWidth="lg">
        <Typography 
          variant="h3" 
          sx={{ 
            mb: 4, 
            color: '#333',
            fontWeight: 700,
            textAlign: 'center'
          }}
        >
          Site Navigation
        </Typography>

        <Grid container spacing={3}>
          {pageGroups.map((group, index) => (
            <Grid item xs={12} md={6} key={index}>
              <PageSection>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 3, 
                    color: '#000',
                    fontWeight: 600 
                  }}
                >
                  {group.title}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                {group.pages.map((page, pageIndex) => (
                  <PageButton
                    key={pageIndex}
                    variant="outlined"
                    startIcon={page.icon}
                    onClick={() => navigate(page.path)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Typography>{page.name}</Typography>
                      <Typography 
                        sx={{ 
                          color: '#666',
                          fontSize: '0.875rem'
                        }}
                      >
                        {page.path}
                      </Typography>
                    </Box>
                  </PageButton>
                ))}
              </PageSection>
            </Grid>
          ))}
        </Grid>

        {/* Quick Stats */}
        <Paper 
          sx={{ 
            mt: 4, 
            p: 3, 
            textAlign: 'center',
            backgroundColor: '#000',
            color: '#FFD700',
            borderRadius: '10px'
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Total Pages Available: {pageGroups.reduce((acc, group) => acc + group.pages.length, 0)}
          </Typography>
          <Typography variant="body1">
            All pages are accessible through this navigation dashboard
          </Typography>
        </Paper>
      </Container>
    </StyledContainer>
  );
};

export default Pages; 